import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Logo } from "@/components/Logo";
import { waLink } from "@/lib/whatsapp";

const LINKS = [
  { to: "/", key: "home" },
  { to: "/about", key: "about" },
  { to: "/services", key: "services" },
  { to: "/portfolio", key: "portfolio" },
  { to: "/contact", key: "contact" },
];

const LangToggle = ({ dark = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid="navbar-lang-toggle"
      className={`flex items-center rounded-full border p-0.5 ${dark ? "border-offwhite/30" : "border-sand bg-offwhite/70"}`}
    >
      {["en", "id"].map((code) => (
        <button
          key={code}
          data-testid={`lang-toggle-${code}`}
          onClick={() => setLang(code)}
          className={`rounded-full px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.15em] transition-colors duration-300 ${
            lang === code
              ? "bg-espresso text-offwhite"
              : dark
                ? "text-offwhite/60 hover:text-offwhite"
                : "text-taupe hover:text-espresso"
          }`}
        >
          {code}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "border-b border-sand/70 bg-offwhite/85 backdrop-blur-md"
            : "border-b border-transparent bg-offwhite/40 backdrop-blur-sm"
        }`}
        data-testid="navbar"
      >
        <div className="wrap flex h-[72px] items-center justify-between">
          <Link to="/" data-testid="navbar-logo-link" className="group flex items-center gap-3">
            <Logo className="h-8 w-8 text-espresso transition-transform duration-500 group-hover:rotate-90" />
            <span className="font-display text-base sm:text-lg font-medium tracking-[0.18em] text-espresso">
              SAMASTA STUDIO
            </span>
          </Link>

          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.key}
                to={l.to}
                data-testid={`navbar-menu-${l.key}`}
                className={({ isActive }) =>
                  `relative text-sm font-normal transition-colors duration-300 ${
                    isActive ? "text-espresso" : "text-charcoal/55 hover:text-espresso"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="relative">
                    {t.nav[l.key]}
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute -bottom-1.5 left-0 h-px w-full bg-teak"
                      />
                    )}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 sm:gap-4">
            <LangToggle />
            <a
              href={waLink(t.nav.waMessage)}
              target="_blank"
              rel="noreferrer"
              data-testid="navbar-konsultasi-cta-button"
              className="hidden items-center gap-2 rounded-full bg-espresso px-5 py-2.5 text-sm font-medium text-offwhite transition-colors duration-300 hover:bg-teak sm:inline-flex"
            >
              {t.nav.cta}
              <ArrowUpRight className="h-4 w-4" />
            </a>
            <button
              data-testid="mobile-menu-trigger"
              onClick={() => setOpen(true)}
              aria-label={t.nav.menuLabel}
              className="grid h-10 w-10 place-items-center rounded-full border border-sand text-espresso lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] flex flex-col bg-espresso text-offwhite"
            data-testid="mobile-menu"
          >
            <div className="wrap flex h-[72px] shrink-0 items-center justify-between">
              <span className="flex items-center gap-3">
                <Logo className="h-8 w-8 text-offwhite" />
                <span className="font-display text-base font-medium tracking-[0.18em]">
                  SAMASTA STUDIO
                </span>
              </span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label={t.nav.closeLabel}
                className="grid h-10 w-10 place-items-center rounded-full border border-offwhite/25"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="wrap flex flex-1 flex-col justify-center gap-2">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.key}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-menu-${l.key}`}
                    className={({ isActive }) =>
                      `block border-b border-offwhite/10 py-4 font-display text-4xl font-light transition-colors sm:text-5xl ${
                        isActive ? "text-terracotta" : "text-offwhite hover:text-terracotta"
                      }`
                    }
                  >
                    {t.nav[l.key]}
                  </NavLink>
                </motion.div>
              ))}
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="wrap flex items-center justify-between gap-4 pb-10"
            >
              <LangToggle dark />
              <a
                href={waLink(t.nav.waMessage)}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-konsultasi-cta-button"
                className="inline-flex items-center gap-2 rounded-full bg-teak px-6 py-3 text-sm font-medium text-offwhite"
              >
                {t.nav.cta}
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
